import * as Tabs from '@radix-ui/react-tabs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublicOtpSettings, PublicProviderConfig } from '@details-ai/contracts';
import {
  Bot,
  Check,
  KeyRound,
  LockKeyhole,
  LoaderCircle,
  Mail,
  MessageCircle,
  RefreshCw,
  Save,
  ShieldCheck,
  TestTube2,
  WalletCards,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';

type ProviderForm = {
  apiKey: string;
  baseUrl: string;
  enabled: boolean;
};

function ProviderCard({
  provider,
  onRefresh,
}: {
  provider: PublicProviderConfig;
  onRefresh: () => Promise<void>;
}) {
  const [form, setForm] = useState<ProviderForm>({
    apiKey: '',
    baseUrl: provider.baseUrl || '',
    enabled: provider.enabled,
  });
  const [busy, setBusy] = useState(false);
  const title = provider.kind === 'OPENAI' ? 'OpenAI' : 'DeepSeek';

  async function save() {
    setBusy(true);
    try {
      await api('/api/admin/providers', {
        method: 'PUT',
        body: JSON.stringify({
          kind: provider.kind,
          enabled: form.enabled,
          ...(form.apiKey ? { apiKey: form.apiKey } : {}),
          ...(form.baseUrl ? { baseUrl: form.baseUrl } : {}),
        }),
      });
      setForm((current) => ({ ...current, apiKey: '' }));
      toast.success(`تم حفظ إعداد ${title}`);
      await onRefresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر حفظ الإعداد');
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setBusy(true);
    try {
      await api('/api/admin/providers/test', {
        method: 'POST',
        body: JSON.stringify({ kind: provider.kind }),
      });
      toast.success(`اتصال ${title} ناجح`);
      await onRefresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر اختبار المزود');
    } finally {
      setBusy(false);
    }
  }

  async function syncModels() {
    setBusy(true);
    try {
      const result = await api<{ synced: number }>('/api/admin/providers/sync', {
        method: 'POST',
        body: JSON.stringify({ kind: provider.kind }),
      });
      toast.success(`تمت مزامنة ${result.synced} نموذجًا`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر مزامنة النماذج');
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="rounded-2xl border border-ink/10 bg-white p-5">
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-ink text-white">
            <Bot className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-black">{title}</h3>
            <p className={`mt-1 text-[10px] font-bold ${provider.configured ? 'text-emerald-600' : 'text-amber-600'}`}>
              {provider.configured ? 'المفتاح محفوظ ومشفر' : 'غير مهيأ'}
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-[10px] font-black text-slate-500">
          مفعل
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
            className="h-4 w-4 accent-teal"
          />
        </label>
      </div>
      <label className="block">
        <span className="mb-1.5 block text-[10px] font-black text-slate-500">API Key</span>
        <input
          type="password"
          value={form.apiKey}
          onChange={(event) => setForm({ ...form, apiKey: event.target.value })}
          placeholder={provider.configured ? 'اتركه فارغًا للاحتفاظ بالمفتاح الحالي' : 'أدخل المفتاح مرة واحدة'}
          dir="ltr"
          className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal"
        />
      </label>
      <label className="mt-3 block">
        <span className="mb-1.5 block text-[10px] font-black text-slate-500">Base URL</span>
        <input
          value={form.baseUrl}
          onChange={(event) => setForm({ ...form, baseUrl: event.target.value })}
          dir="ltr"
          className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal"
        />
      </label>
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-ink px-3 py-2.5 text-[10px] font-black text-white hover:bg-teal disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" /> حفظ
        </button>
        <button
          type="button"
          onClick={() => void test()}
          disabled={busy || !provider.configured}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-ink/10 px-3 py-2.5 text-[10px] font-black text-slate-600 hover:border-teal/30 hover:text-teal disabled:opacity-40"
        >
          {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <TestTube2 className="h-3.5 w-3.5" />}
          اختبار
        </button>
      </div>
      <button
        type="button"
        onClick={() => void syncModels()}
        disabled={busy || !provider.configured}
        className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-ink/10 px-3 py-2 text-[10px] font-black text-slate-500 hover:border-teal/30 hover:text-teal disabled:opacity-40"
      >
        <RefreshCw className="h-3 w-3" /> مزامنة النماذج
      </button>
      {provider.lastTestedAt && (
        <p className="mt-3 text-[9px] font-bold text-slate-400">
          آخر اختبار: {new Intl.DateTimeFormat('ar-SA', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(provider.lastTestedAt))}
          {' · '} {provider.lastTestStatus === 'SUCCESS' ? 'ناجح' : 'فاشل'}
        </p>
      )}
    </article>
  );
}

function OtpSettingsPanel({
  settings,
  onRefresh,
}: {
  settings: PublicOtpSettings;
  onRefresh: () => Promise<void>;
}) {
  const [form, setForm] = useState(settings);
  const [smtpPassword, setSmtpPassword] = useState('');
  const [whatsappToken, setWhatsappToken] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => setForm(settings), [settings]);

  async function save() {
    setBusy(true);
    try {
      const payload = {
        enabled: form.enabled,
        primaryChannel: form.primaryChannel,
        ...(form.adminEmail ? { adminEmail: form.adminEmail } : {}),
        ...(form.adminWhatsapp ? { adminWhatsapp: form.adminWhatsapp } : {}),
        codeTtlMinutes: form.codeTtlMinutes,
        maxAttempts: form.maxAttempts,
        resendCooldownSeconds: form.resendCooldownSeconds,
        email: {
          enabled: form.email.enabled,
          ...(form.email.smtpHost ? { smtpHost: form.email.smtpHost } : {}),
          ...(form.email.smtpPort ? { smtpPort: form.email.smtpPort } : {}),
          smtpSecure: form.email.smtpSecure,
          ...(form.email.smtpUser ? { smtpUser: form.email.smtpUser } : {}),
          ...(smtpPassword ? { smtpPassword } : {}),
          ...(form.email.fromName ? { fromName: form.email.fromName } : {}),
          ...(form.email.fromEmail ? { fromEmail: form.email.fromEmail } : {}),
        },
        whatsapp: {
          enabled: form.whatsapp.enabled,
          ...(form.whatsapp.baseUrl ? { baseUrl: form.whatsapp.baseUrl } : {}),
          ...(form.whatsapp.sendPath ? { sendPath: form.whatsapp.sendPath } : {}),
          ...(whatsappToken ? { token: whatsappToken } : {}),
          ...(form.whatsapp.senderId ? { senderId: form.whatsapp.senderId } : {}),
        },
      };
      await api('/api/admin/otp-settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setSmtpPassword('');
      setWhatsappToken('');
      toast.success('تم حفظ إعدادات التحقق');
      await onRefresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر حفظ إعدادات التحقق');
    } finally {
      setBusy(false);
    }
  }

  async function test(channel: 'EMAIL' | 'WHATSAPP') {
    setBusy(true);
    try {
      await api('/api/admin/otp-settings/test', {
        method: 'POST',
        body: JSON.stringify({ channel }),
      });
      toast.success('تم إرسال رسالة اختبار');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر إرسال الاختبار');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <section className={`rounded-2xl border p-5 ${form.enabled ? 'border-teal/20 bg-teal/5' : 'border-amber-200 bg-amber-50'}`}>
        <div className="flex items-start gap-3">
          <ShieldCheck className={`mt-0.5 h-5 w-5 ${form.enabled ? 'text-teal' : 'text-amber-600'}`} />
          <div className="flex-1">
            <h3 className="text-sm font-black">
              {form.enabled ? 'OTP مفعّل لحساب المشرف' : 'الدخول حاليًا بكلمة المرور فقط'}
            </h3>
            <p className="mt-1 text-[11px] font-semibold leading-5 text-slate-600">
              لن يتم تفعيل OTP قبل اكتمال إعداد القناة الأساسية. لا تُعرض كلمات المرور أو التوكنات بعد حفظها.
            </p>
          </div>
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
            className="mt-1 h-4 w-4 accent-teal"
          />
        </div>
      </section>

      <section className="rounded-2xl border border-ink/10 bg-white p-5">
        <h3 className="mb-4 text-sm font-black">وجهة استلام الرمز</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <label>
            <span className="mb-1.5 block text-[10px] font-black text-slate-500">البريد الإلكتروني</span>
            <input
              dir="ltr"
              value={form.adminEmail || ''}
              onChange={(event) => setForm({ ...form, adminEmail: event.target.value })}
              className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal"
              placeholder="admin@example.com"
            />
          </label>
          <label>
            <span className="mb-1.5 block text-[10px] font-black text-slate-500">رقم WhatsApp</span>
            <input
              dir="ltr"
              value={form.adminWhatsapp || ''}
              onChange={(event) => setForm({ ...form, adminWhatsapp: event.target.value })}
              className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal"
              placeholder="+9665..."
            />
          </label>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <label>
            <span className="mb-1.5 block text-[10px] font-black text-slate-500">القناة الأساسية</span>
            <select
              value={form.primaryChannel}
              onChange={(event) => setForm({ ...form, primaryChannel: event.target.value as 'EMAIL' | 'WHATSAPP' })}
              className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal"
            >
              <option value="EMAIL">البريد الإلكتروني</option>
              <option value="WHATSAPP">WhatsApp</option>
            </select>
          </label>
          <label>
            <span className="mb-1.5 block text-[10px] font-black text-slate-500">صلاحية الرمز (دقيقة)</span>
            <input
              type="number"
              min={2}
              max={30}
              value={form.codeTtlMinutes}
              onChange={(event) => setForm({ ...form, codeTtlMinutes: Number(event.target.value) })}
              className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal"
            />
          </label>
          <label>
            <span className="mb-1.5 block text-[10px] font-black text-slate-500">أقصى المحاولات</span>
            <input
              type="number"
              min={3}
              max={10}
              value={form.maxAttempts}
              onChange={(event) => setForm({ ...form, maxAttempts: Number(event.target.value) })}
              className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal"
            />
          </label>
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-2">
        <section className="rounded-2xl border border-ink/10 bg-white p-5">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-teal" />
              <h3 className="text-sm font-black">بريد Details Mail</h3>
            </div>
            <label className="flex items-center gap-2 text-[10px] font-black text-slate-500">
              مفعل
              <input
                type="checkbox"
                checked={form.email.enabled}
                onChange={(event) => setForm({ ...form, email: { ...form.email, enabled: event.target.checked } })}
                className="h-4 w-4 accent-teal"
              />
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {([
              ['smtpHost', 'SMTP Host', 'smtp.details-mail.com'],
              ['smtpUser', 'SMTP User', 'noreply@details-mail.com'],
              ['fromName', 'اسم المرسل', 'Details AI'],
              ['fromEmail', 'بريد المرسل', 'noreply@details-mail.com'],
            ] as const).map(([key, label, placeholder]) => (
              <label key={key}>
                <span className="mb-1 block text-[9px] font-black text-slate-500">{label}</span>
                <input
                  value={String(form.email[key as keyof typeof form.email] || '')}
                  onChange={(event) => setForm({ ...form, email: { ...form.email, [key]: event.target.value } })}
                  dir={key === 'fromName' ? 'rtl' : 'ltr'}
                  placeholder={placeholder}
                  className="w-full rounded-lg border border-ink/10 bg-slate-50 px-3 py-2 text-[11px] outline-none focus:border-teal"
                />
              </label>
            ))}
            <label>
              <span className="mb-1 block text-[9px] font-black text-slate-500">SMTP Port</span>
              <input
                type="number"
                value={form.email.smtpPort || 465}
                onChange={(event) => setForm({ ...form, email: { ...form.email, smtpPort: Number(event.target.value) } })}
                className="w-full rounded-lg border border-ink/10 bg-slate-50 px-3 py-2 text-[11px] outline-none focus:border-teal"
              />
            </label>
            <label>
              <span className="mb-1 block text-[9px] font-black text-slate-500">كلمة مرور SMTP</span>
              <input
                type="password"
                value={smtpPassword}
                onChange={(event) => setSmtpPassword(event.target.value)}
                placeholder={form.email.configured ? 'محفوظة — اتركها فارغة' : 'أدخل كلمة المرور'}
                dir="ltr"
                className="w-full rounded-lg border border-ink/10 bg-slate-50 px-3 py-2 text-[11px] outline-none focus:border-teal"
              />
            </label>
          </div>
          <button
            type="button"
            onClick={() => void test('EMAIL')}
            disabled={busy || !form.email.configured}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-ink/10 px-3 py-2 text-[10px] font-black text-slate-600 disabled:opacity-40"
          >
            <TestTube2 className="h-3 w-3" /> اختبار البريد
          </button>
        </section>

        <section className="rounded-2xl border border-ink/10 bg-white p-5">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-teal" />
              <h3 className="text-sm font-black">WhatsApp API</h3>
            </div>
            <label className="flex items-center gap-2 text-[10px] font-black text-slate-500">
              مفعل
              <input
                type="checkbox"
                checked={form.whatsapp.enabled}
                onChange={(event) => setForm({ ...form, whatsapp: { ...form.whatsapp, enabled: event.target.checked } })}
                className="h-4 w-4 accent-teal"
              />
            </label>
          </div>
          <div className="space-y-3">
            <input
              value={form.whatsapp.baseUrl || ''}
              onChange={(event) => setForm({ ...form, whatsapp: { ...form.whatsapp, baseUrl: event.target.value } })}
              placeholder="https://whatsapp-api.internal"
              dir="ltr"
              className="w-full rounded-lg border border-ink/10 bg-slate-50 px-3 py-2 text-[11px] outline-none focus:border-teal"
            />
            <input
              value={form.whatsapp.sendPath || '/messages/send'}
              onChange={(event) => setForm({ ...form, whatsapp: { ...form.whatsapp, sendPath: event.target.value } })}
              placeholder="/messages/send"
              dir="ltr"
              className="w-full rounded-lg border border-ink/10 bg-slate-50 px-3 py-2 text-[11px] outline-none focus:border-teal"
            />
            <input
              value={form.whatsapp.senderId || ''}
              onChange={(event) => setForm({ ...form, whatsapp: { ...form.whatsapp, senderId: event.target.value } })}
              placeholder="Sender ID"
              dir="ltr"
              className="w-full rounded-lg border border-ink/10 bg-slate-50 px-3 py-2 text-[11px] outline-none focus:border-teal"
            />
            <input
              type="password"
              value={whatsappToken}
              onChange={(event) => setWhatsappToken(event.target.value)}
              placeholder={form.whatsapp.configured ? 'التوكن محفوظ — اتركه فارغًا' : 'API Token'}
              dir="ltr"
              className="w-full rounded-lg border border-ink/10 bg-slate-50 px-3 py-2 text-[11px] outline-none focus:border-teal"
            />
          </div>
          <button
            type="button"
            onClick={() => void test('WHATSAPP')}
            disabled={busy || !form.whatsapp.configured}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-ink/10 px-3 py-2 text-[10px] font-black text-slate-600 disabled:opacity-40"
          >
            <TestTube2 className="h-3 w-3" /> اختبار WhatsApp
          </button>
        </section>
      </div>

      <button
        type="button"
        onClick={() => void save()}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-xl bg-ink px-5 py-3 text-xs font-black text-white hover:bg-teal disabled:opacity-50"
      >
        {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        حفظ إعدادات التحقق
      </button>
    </div>
  );
}

function PasswordPanel() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (newPassword.length < 12) {
      toast.error('كلمة المرور الجديدة يجب ألا تقل عن 12 حرفًا');
      return;
    }
    if (newPassword !== confirmation) {
      toast.error('تأكيد كلمة المرور غير مطابق');
      return;
    }
    setBusy(true);
    try {
      await api('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      toast.success('تم تغيير كلمة المرور. سجّل الدخول من جديد.');
      window.location.assign('/admin/login');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر تغيير كلمة المرور');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="max-w-xl rounded-2xl border border-ink/10 bg-white p-5">
      <div className="mb-5 flex items-start gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-teal/10 text-teal">
          <LockKeyhole className="h-4 w-4" />
        </div>
        <div>
          <h3 className="text-sm font-black">تغيير كلمة مرور المشرف</h3>
          <p className="mt-1 text-[10px] font-semibold leading-5 text-slate-500">
            بعد الحفظ تُلغى كل الجلسات الحالية ويُطلب تسجيل الدخول من جديد.
          </p>
        </div>
      </div>
      <div className="space-y-3">
        {[
          { label: 'كلمة المرور الحالية', value: currentPassword, set: setCurrentPassword, complete: 'current-password' },
          { label: 'كلمة المرور الجديدة', value: newPassword, set: setNewPassword, complete: 'new-password' },
          { label: 'تأكيد كلمة المرور', value: confirmation, set: setConfirmation, complete: 'new-password' },
        ].map((field) => (
          <label key={field.label} className="block">
            <span className="mb-1.5 block text-[10px] font-black text-slate-500">{field.label}</span>
            <input
              type="password"
              value={field.value}
              onChange={(event) => field.set(event.target.value)}
              autoComplete={field.complete}
              dir="ltr"
              className="w-full rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal"
            />
          </label>
        ))}
      </div>
      <button
        type="submit"
        disabled={busy || !currentPassword || !newPassword || !confirmation}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2.5 text-[10px] font-black text-white hover:bg-teal disabled:opacity-50"
      >
        {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <LockKeyhole className="h-3.5 w-3.5" />}
        حفظ كلمة المرور
      </button>
    </form>
  );
}

export function AdminPage() {
  const queryClient = useQueryClient();
  const otpQuery = useQuery({
    queryKey: ['otp-settings'],
    queryFn: () => api<PublicOtpSettings>('/api/admin/otp-settings'),
  });
  const providersQuery = useQuery({
    queryKey: ['providers'],
    queryFn: () => api<PublicProviderConfig[]>('/api/admin/providers'),
  });
  const usageQuery = useQuery({
    queryKey: ['usage'],
    queryFn: () =>
      api<{
        periodDays: number;
        total: {
          _count: number;
          _sum: { inputTokens: number | null; outputTokens: number | null; costUsd: number | null };
        };
      }>('/api/admin/usage-summary'),
  });

  async function refreshOtp() {
    await queryClient.invalidateQueries({ queryKey: ['otp-settings'] });
  }
  async function refreshProviders() {
    await queryClient.invalidateQueries({ queryKey: ['providers'] });
  }

  return (
    <div className="min-h-screen p-4 sm:p-8">
      <div className="mx-auto max-w-6xl">
        <header className="mb-7">
          <p className="text-xs font-black text-teal">مركز التحكم</p>
          <h1 className="mt-2 text-2xl font-black">الإدارة والمزودون</h1>
          <p className="mt-2 text-xs font-semibold text-slate-500">
            التحقق، مفاتيح مزودي الذكاء الاصطناعي، والاستهلاك في مكان واحد.
          </p>
        </header>

        <Tabs.Root defaultValue="otp">
          <Tabs.List className="mb-5 flex w-full gap-1 overflow-x-auto rounded-xl border border-ink/10 bg-white p-1">
            {([
              ['otp', 'التحقق وOTP', ShieldCheck],
              ['providers', 'مزودو AI', KeyRound],
              ['usage', 'الاستهلاك', WalletCards],
              ['security', 'أمان المشرف', LockKeyhole],
            ] as const).map(([value, label, Icon]) => (
              <Tabs.Trigger
                key={String(value)}
                value={String(value)}
                className="inline-flex min-w-fit items-center gap-2 rounded-lg px-4 py-2.5 text-[11px] font-black text-slate-500 outline-none transition data-[state=active]:bg-ink data-[state=active]:text-white"
              >
                <Icon className="h-3.5 w-3.5" />
                {String(label)}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          <Tabs.Content value="otp" className="outline-none">
            {otpQuery.isLoading ? (
              <div className="grid min-h-64 place-items-center"><LoaderCircle className="h-6 w-6 animate-spin text-teal" /></div>
            ) : otpQuery.data ? (
              <OtpSettingsPanel settings={otpQuery.data} onRefresh={refreshOtp} />
            ) : (
              <p className="rounded-xl bg-red-50 p-4 text-xs font-bold text-red-700">تعذر تحميل إعدادات التحقق</p>
            )}
          </Tabs.Content>
          <Tabs.Content value="providers" className="outline-none">
            <div className="mb-4 rounded-2xl border border-ink/10 bg-white/70 p-4 text-xs font-semibold leading-6 text-slate-500">
              المفاتيح تُشفّر قبل الحفظ ولا تُعاد إلى المتصفح. اترك الحقل فارغًا للإبقاء على المفتاح الحالي.
              المحادثات تبقى معطلة حتى ينجح اختبار مزود واحد على الأقل.
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {providersQuery.data?.map((provider) => (
                <ProviderCard key={provider.kind} provider={provider} onRefresh={refreshProviders} />
              ))}
            </div>
          </Tabs.Content>
          <Tabs.Content value="usage" className="outline-none">
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                { label: 'عمليات AI', value: usageQuery.data?.total._count ?? 0, Icon: Bot },
                { label: 'رموز الإدخال', value: usageQuery.data?.total._sum.inputTokens ?? 0, Icon: Check },
                { label: 'رموز الإخراج', value: usageQuery.data?.total._sum.outputTokens ?? 0, Icon: MessageCircle },
              ].map(({ label, value, Icon }) => (
                <article key={label} className="rounded-2xl border border-ink/10 bg-white p-5">
                  <Icon className="mb-4 h-5 w-5 text-teal" />
                  <p className="text-2xl font-black">{Number(value).toLocaleString('ar-SA')}</p>
                  <p className="mt-1 text-[10px] font-bold text-slate-500">{label} · آخر 30 يومًا</p>
                </article>
              ))}
            </div>
            <div className="mt-4 rounded-2xl border border-ink/10 bg-white p-5">
              <h3 className="text-sm font-black">التكلفة المسجلة</h3>
              <p className="mt-3 text-3xl font-black text-teal">
                ${Number(usageQuery.data?.total._sum.costUsd ?? 0).toFixed(2)}
              </p>
              <p className="mt-2 text-[10px] font-semibold text-slate-500">
                تظهر التكلفة الفعلية بعد ربط جدول أسعار النماذج وإدخال مفاتيح المزودين.
              </p>
            </div>
          </Tabs.Content>
          <Tabs.Content value="security" className="outline-none">
            <PasswordPanel />
          </Tabs.Content>
        </Tabs.Root>
      </div>
    </div>
  );
}
